import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  Play,
  CheckCircle2,
  Star,
  BookOpen,
  Clock,
  Award,
  Menu,
  X,
  Instagram,
  Twitter,
  Github,
  Linkedin,
  ArrowRight,
  Calendar,
  CreditCard,
  FileText,
  Library,
  Bell,
  User,
  GraduationCap,
  Layout,
  Search,
  MoreVertical,
  Briefcase,
  PieChart,
} from 'lucide-react';
import '../styles/landing-page.scss';

const cn = (...classes) => classes.filter(Boolean).join(' ');

// --- Components ---

const Navbar = ({ onLoginClick, onSignupClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#' },
    { name: 'Features', href: '#features' },
    { name: 'Services', href: '#services' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <nav className={cn('navbar', isScrolled ? 'navbar--scrolled' : '')}>
      <div className="navbar__container">
       

        <div className="navbar__links-desktop">
          <div className="navbar__logo">
            <div className="navbar__logo-icon">
              <span></span>
            </div>
            <span className="navbar__logo-text">EduGenius</span>
          </div>
          {navLinks.map((link) => (
            <a key={link.name} href={link.href} className="navbar__link">
              {link.name}
            </a>
          ))}
            <div className="navbar__actions-desktop">
          <button className="navbar__login-btn" onClick={onLoginClick}>
            Login
          </button>
          <button className="navbar__signup-btn" onClick={onSignupClick}>
            Sign up
          </button>
        </div>
        </div>



        <div className="navbar__mobile-toggle">
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="navbar__menu-btn">
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="navbar__mobile-menu"
          >
            <div className="navbar__mobile-content">
              {navLinks.map((link) => (
                <a key={link.name} href={link.href} className="navbar__mobile-link">
                  {link.name}
                </a>
              ))}
              <div className="navbar__mobile-actions">
                <button className="navbar__mobile-login" onClick={onLoginClick}>
                  Login
                </button>
                <button className="navbar__mobile-signup" onClick={onSignupClick}>
                  Sign up
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const Hero = ({ onLoginClick }) => {
  return (
    <section className="hero">
      <div className="hero__background">
        <div className="hero__blob hero__blob--1"></div>
        <div className="hero__blob hero__blob--2"></div>
      </div>

      <div className="hero__container">
        <div className="hero__content">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="hero__badge"
          >
            <Star size={14} className="hero__badge-icon" /> Trusted by 100+ Institutions
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="hero__title"
          >
            One Login for <span className="hero__title-highlight">Everything</span> Academic.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hero__subtitle"
          >
            Access Attendance, Fee Payments, Academic Results, and Course Materials in one
            high-performance dashboard. The future of student management is here.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hero__actions"
          >
            <button className="hero__primary-btn" onClick={onLoginClick}>
              Portal Login <ChevronRight size={18} />
            </button>
            <button className="hero__secondary-btn">
              <div className="hero__play-icon">
                <Play size={16} fill="white" />
              </div>
              Explore Features
            </button>
          </motion.div>
        </div>

        <div className="hero__cards">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="hero__card hero__card--1"
          >
            <div className="hero__card-icon">
              <ArrowRight className="hero__card-icon-inner" size={24} />
            </div>
            <div className="hero__card-content">
              <h3 className="hero__card-title">Empower Your Educational Institution</h3>
              <p className="hero__card-subtitle">School Manager Pro</p>
            </div>
            <div className="hero__card-chart">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="hero__card-bar"
                  style={{ height: `${Math.random() * 40 + 20}%` }}
                />
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="hero__card hero__card--2"
          >
            <div className="hero__card-stat">260%</div>
            <p className="hero__card-stat-label">Your ultimate SaaS solution for effortless</p>
            <div className="hero__card-dots">
              <div className="hero__card-dot"></div>
              <div className="hero__card-dot hero__card-dot--medium"></div>
              <div className="hero__card-dot hero__card-dot--light"></div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="hero__card hero__card--3"
          >
            <img
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=800&auto=format&fit=crop"
              alt="Students"
              className="hero__card-image"
            />
            <div className="hero__card-overlay"></div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="hero__card hero__card--4"
          >
            <div className="hero__card-progress-stat">98%</div>
            <div className="hero__card-progress-content">
              <p className="hero__card-progress-label">Success rate</p>
              <p className="hero__card-progress-text">Our comprehensive suite of tools and support.</p>
            </div>
            <div className="hero__card-progress-bar">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: '98%' }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                className="hero__card-progress-fill"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FeatureSection1 = () => {
  return (
    <section className="feature-section feature-section--white">
      <div className="feature-section__container">
        <div className="feature-section__content">
          <div className="feature-section__left">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="feature-section__card-wrapper"
            >
              <div className="feature-section__card">
                <div className="feature-section__card-header">
                  <h4 className="feature-section__card-title">Today's course</h4>
                  <span className="feature-section__card-menu">...</span>
                </div>
                <div className="feature-section__card-course">
                  <div className="feature-section__course-progress">
                    <div className="feature-section__progress-circle">71%</div>
                    <div className="feature-section__course-info">
                      <div className="feature-section__course-name">Web Development</div>
                      <div className="feature-section__course-meta">
                        <span className="feature-section__meta-item">
                          <BookOpen size={10} /> 4 lesson
                        </span>
                        <span className="feature-section__meta-item">
                          <Clock size={10} /> 30 min
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="feature-section__course-buttons">
                    <button className="feature-section__btn-skip">Skip</button>
                    <button className="feature-section__btn-continue">Continue</button>
                  </div>
                </div>
                <div className="feature-section__card-placeholder"></div>
              </div>
            </motion.div>
          </div>

          <div className="feature-section__right">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="feature-section__title">
                Empower Institution with Our Management Solutions
              </h2>
              <p className="feature-section__description">
                Experience a Paradigm Shift in School Management with Our Cutting-Edge Solutions
                and Unparalleled Support. Empowering Education for a Brighter Tomorrow.
              </p>
              <div className="feature-section__buttons">
                <button className="feature-section__btn-primary">Get started</button>
                <button className="feature-section__btn-secondary">See How it works</button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PortalServicesGrid = () => {
  const services = [
    { name: 'Fee Payment', icon: CreditCard, desc: 'Secure online fee processing', color: 'emerald' },
    { name: 'Admit Card', icon: FileText, desc: 'Instant exam hall tickets', color: 'blue' },
    { name: 'Results', icon: Award, desc: 'Academic records at a click', color: 'purple' },
    { name: 'Attendance', icon: PieChart, desc: 'Track your daily presence', color: 'orange' },
    { name: 'Hostel', icon: Layout, desc: 'Manage room and mess details', color: 'pink' },
    { name: 'Library', icon: Library, desc: 'Search and reserve books', color: 'indigo' },
    { name: 'Placement', icon: Briefcase, desc: 'Career drives and interviews', color: 'cyan' },
    { name: 'Scholarship', icon: GraduationCap, desc: 'Track financial aid status', color: 'red' },
  ];

  return (
    <section id="services" className="services-section">
      <div className="services-section__container">
        <div className="services-section__grid">
          <div className="services-section__left">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="services-section__content"
            >
              <div className="services-section__badge">
                <Search size={14} /> Comprehensive Coverage
              </div>
              <h2 className="services-section__title">
                All Campus Services, <span className="services-section__title-highlight">Single Login.</span>
              </h2>
              <p className="services-section__description">
                Experience the convenience of having every institutional service just a click away.
                From finance management to placement records, we've digitized the entire student journey.
              </p>
              <div className="services-section__features">
                <div className="services-section__feature-item">
                  <div className="services-section__feature-check">
                    <CheckCircle2 size={12} />
                  </div>
                  <span className="services-section__feature-text">Digital ID Card</span>
                </div>
                <div className="services-section__feature-item">
                  <div className="services-section__feature-check">
                    <CheckCircle2 size={12} />
                  </div>
                  <span className="services-section__feature-text">E-Resources</span>
                </div>
                <div className="services-section__feature-item">
                  <div className="services-section__feature-check">
                    <CheckCircle2 size={12} />
                  </div>
                  <span className="services-section__feature-text">No Dues Certificate</span>
                </div>
                <div className="services-section__feature-item">
                  <div className="services-section__feature-check">
                    <CheckCircle2 size={12} />
                  </div>
                  <span className="services-section__feature-text">Exam History</span>
                </div>
              </div>
              <div className="services-section__cta">
                <button className="services-section__cta-btn">
                  Visit Student Hub <ArrowRight size={18} />
                </button>
              </div>
            </motion.div>
          </div>

          <div className="services-section__grid-right">
            {services.map((service, idx) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className={cn('services-grid__item', `services-grid__item--${service.color}`)}
              >
                <div className="services-grid__icon">
                  <service.icon size={24} />
                </div>
                <h4 className="services-grid__name">{service.name}</h4>
                <p className="services-grid__desc">{service.desc}</p>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.45 }}
              className="services-grid__more"
            >
              <MoreVertical size={24} />
              <span>More Services</span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const DashboardPreview = () => {
  const quickLinks = [
    { name: 'Pay Fee', icon: CreditCard, color: 'emerald' },
    { name: 'Results', icon: FileText, color: 'blue' },
    { name: 'Attendance', icon: PieChart, color: 'orange' },
    { name: 'Library', icon: Library, color: 'purple' },
    { name: 'Hostel', icon: Layout, color: 'pink' },
    { name: 'Events', icon: Calendar, color: 'indigo' },
  ];

  return (
    <section id="features" className="dashboard-section">
      <div className="dashboard-section__background"></div>

      <div className="dashboard-section__header">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="dashboard-section__badge"
        >
          <GraduationCap size={16} /> Student Centric Experience
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="dashboard-section__title"
        >
          A Complete Ecosystem <br /> for Modern Learners
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="dashboard-section__description"
        >
          Inspired by high-performance educational portals, we provide everything from fee payments
          to academic tracking in a single, beautiful interface.
        </motion.p>
      </div>

      <div className="dashboard-section__container">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="dashboard-section__preview"
        >
          <div className="dashboard-preview__header">
            <div className="dashboard-preview__header-left">
              <div className="dashboard-preview__logo">
                <Layout size={20} />
              </div>
              <span className="dashboard-preview__logo-text">StudentPortal</span>
              <div className="dashboard-preview__nav">
                <button className="dashboard-preview__nav-active">Dashboard</button>
                <button className="dashboard-preview__nav-item">Courses</button>
                <button className="dashboard-preview__nav-item">Messages</button>
              </div>
            </div>
            <div className="dashboard-preview__header-right">
              <div className="dashboard-preview__notification">
                <Bell size={20} />
                <span className="dashboard-preview__notification-badge"></span>
              </div>
              <div className="dashboard-preview__divider"></div>
              <div className="dashboard-preview__profile">
                <img
                  src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?q=80&w=100&auto=format&fit=crop"
                  alt="Avatar"
                  className="dashboard-preview__avatar"
                />
                <div className="dashboard-preview__profile-info">
                  <div className="dashboard-preview__profile-degree">B.Tech CSE</div>
                  <div className="dashboard-preview__profile-name">Chetan Singh</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-preview__content">
            <div className="dashboard-preview__sidebar">
              <div className="dashboard-preview__quick-links">
                <div className="dashboard-preview__quick-links-title">Quick Links</div>
                <div className="dashboard-preview__quick-grid">
                  {quickLinks.map((link) => (
                    <button
                      key={link.name}
                      className={cn(
                        'dashboard-preview__quick-item',
                        `dashboard-preview__quick-item--${link.color}`
                      )}
                    >
                      <div className="dashboard-preview__quick-icon">
                        <link.icon size={20} />
                      </div>
                      <span className="dashboard-preview__quick-name">{link.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="dashboard-preview__fee-card">
                <h4 className="dashboard-preview__fee-title">Fee Payment</h4>
                <p className="dashboard-preview__fee-session">Academic Session 2023-24</p>
                <div className="dashboard-preview__fee-content">
                  <div>
                    <div className="dashboard-preview__fee-label">Due Amount</div>
                    <div className="dashboard-preview__fee-amount">$1,240.00</div>
                  </div>
                  <button className="dashboard-preview__fee-btn">Pay Now</button>
                </div>
              </div>
            </div>

            <div className="dashboard-preview__main">
              <div className="dashboard-preview__profile-bar">
                <div className="dashboard-preview__profile-left">
                  <div className="dashboard-preview__profile-avatar-large">
                    <User size={32} />
                  </div>
                  <div>
                    <h3 className="dashboard-preview__profile-bar-name">Chetan Singh</h3>
                    <div className="dashboard-preview__profile-badges">
                      <span className="dashboard-preview__badge dashboard-preview__badge--blue">
                        2020BCS1024
                      </span>
                      <span className="dashboard-preview__badge dashboard-preview__badge--green">
                        Semester 7
                      </span>
                    </div>
                  </div>
                </div>
                <div className="dashboard-preview__stats">
                  <div className="dashboard-preview__stat">
                    <div className="dashboard-preview__stat-label">Attendance</div>
                    <div className="dashboard-preview__stat-value">84%</div>
                  </div>
                  <div className="dashboard-preview__stat">
                    <div className="dashboard-preview__stat-label">SGPA</div>
                    <div className="dashboard-preview__stat-value dashboard-preview__stat-value--purple">
                      8.92
                    </div>
                  </div>
                </div>
              </div>

              <div className="dashboard-preview__grid">
                <div className="dashboard-preview__card">
                  <div className="dashboard-preview__card-header">
                    <h4 className="dashboard-preview__card-title">Upcoming Classes</h4>
                    <button className="dashboard-preview__card-link">View All</button>
                  </div>
                  <div className="dashboard-preview__card-content">
                    <div className="dashboard-preview__class-item dashboard-preview__class-item--active">
                      <div className="dashboard-preview__class-icon dashboard-preview__class-icon--purple">
                        <Clock size={16} />
                      </div>
                      <div>
                        <div className="dashboard-preview__class-name">Database Systems</div>
                        <div className="dashboard-preview__class-meta">Room 402 • 10:30 AM</div>
                      </div>
                    </div>
                    <div className="dashboard-preview__class-item dashboard-preview__class-item--inactive">
                      <div className="dashboard-preview__class-icon dashboard-preview__class-icon--blue">
                        <Clock size={16} />
                      </div>
                      <div>
                        <div className="dashboard-preview__class-name">Software Eng.</div>
                        <div className="dashboard-preview__class-meta">Lab 2 • 01:00 PM</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="dashboard-preview__card">
                  <div className="dashboard-preview__card-header">
                    <h4 className="dashboard-preview__card-title">Announcements</h4>
                    <Bell size={16} />
                  </div>
                  <div className="dashboard-preview__card-content">
                    <div className="dashboard-preview__announcement dashboard-preview__announcement--orange">
                      <div className="dashboard-preview__announcement-title">
                        Hostel Mess Timing Updated
                      </div>
                      <div className="dashboard-preview__announcement-time">Posted 2h ago</div>
                    </div>
                    <div className="dashboard-preview__announcement dashboard-preview__announcement--blue">
                      <div className="dashboard-preview__announcement-title">
                        Convocation Registration 2024
                      </div>
                      <div className="dashboard-preview__announcement-time">Posted Yesterday</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard-preview__right-sidebar">
              <div className="dashboard-preview__metrics">
                <h4 className="dashboard-preview__metrics-title">Learning Metrics</h4>
                <div className="dashboard-preview__metrics-list">
                  <div className="dashboard-preview__metric">
                    <div className="dashboard-preview__metric-header">
                      <span className="dashboard-preview__metric-label">Course Progress</span>
                      <span className="dashboard-preview__metric-value">72%</span>
                    </div>
                    <div className="dashboard-preview__metric-bar">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: '72%' }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                        className="dashboard-preview__metric-bar-fill"
                        style={{ background: '#a855f7' }}
                      />
                    </div>
                  </div>
                  <div className="dashboard-preview__metric">
                    <div className="dashboard-preview__metric-header">
                      <span className="dashboard-preview__metric-label">Assignment Completion</span>
                      <span className="dashboard-preview__metric-value">88%</span>
                    </div>
                    <div className="dashboard-preview__metric-bar">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: '88%' }}
                        transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
                        className="dashboard-preview__metric-bar-fill"
                        style={{ background: '#3b82f6' }}
                      />
                    </div>
                  </div>
                  <div className="dashboard-preview__metric">
                    <div className="dashboard-preview__metric-header">
                      <span className="dashboard-preview__metric-label">Library Book Returns</span>
                      <span className="dashboard-preview__metric-value">95%</span>
                    </div>
                    <div className="dashboard-preview__metric-bar">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: '95%' }}
                        transition={{ duration: 1, ease: 'easeOut', delay: 0.4 }}
                        className="dashboard-preview__metric-bar-fill"
                        style={{ background: '#ea580c' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="dashboard-preview__result-card">
                <div className="dashboard-preview__result-header">
                  <h4 className="dashboard-preview__result-title">Academic Result</h4>
                  <Award size={18} />
                </div>
                <div className="dashboard-preview__result-content">
                  <div className="dashboard-preview__result-stat">9.2</div>
                  <div className="dashboard-preview__result-label">Latest SGPA</div>
                </div>
                <div className="dashboard-preview__result-overlay">
                  <div className="dashboard-preview__result-message">
                    <div className="dashboard-preview__result-congrats">Congratulations!</div>
                    <p className="dashboard-preview__result-text">
                      You are in the top 5% of your batch this semester.
                    </p>
                    <button className="dashboard-preview__result-btn">View Marks</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const StepsSection = () => {
  const steps = [
    {
      title: 'Explore Features',
      description:
        'Uncover the Breadth and Depth of EduGenius Powerful Tools and Capabilities.',
    },
    {
      title: 'Navigate Dashboard',
      description:
        'Effortlessly Maneuver through the Intuitive Interface for Seamless Operations.',
    },
    {
      title: 'Optimize Workflow',
      description:
        'Maximize Efficiency and Effectiveness with Streamlined Processes and Automation.',
    },
  ];

  return (
    <section className="steps-section">
      <div className="steps-section__container">
        <div className="steps-section__header">
          <h2 className="steps-section__title">Simplify School Management in 3 Steps</h2>
          <p className="steps-section__description">
            Streamline Operations, Enhance Engagement, and Empower Educators. Discover Seamless
            Efficiency and Innovation in Education Management.
          </p>
        </div>

        <div className="steps-section__content">
          <div className="steps-section__left">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="steps-section__card"
            >
              <div className="steps-section__card-header">
                <h4 className="steps-section__card-title">My assignment</h4>
                <div className="steps-section__checklist">
                  <div className="steps-section__check-item steps-section__check-item--checked"></div>
                  <div className="steps-section__check-item"></div>
                </div>
              </div>
              <div className="steps-section__card-stats">
                <div className="steps-section__stat-box steps-section__stat-box--orange">
                  <div className="steps-section__stat-number">04</div>
                  <div className="steps-section__stat-label">Pending</div>
                </div>
                <div className="steps-section__stat-box steps-section__stat-box--purple">
                  <div className="steps-section__stat-number">08</div>
                  <div className="steps-section__stat-label">Courses</div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="steps-section__right">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="steps-section__step"
              >
                <h3 className="steps-section__step-title">{step.title}</h3>
                <p className="steps-section__step-description">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const reviews = [
    {
      name: 'Theresa Webb',
      role: 'CEO at wolf pixel',
      text: 'EduGenius has truly transformed how we manage our school\'s operations. The platform is intuitive, easy to use, and has streamlined.',
      image:
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=200&auto=format&fit=crop',
    },
    {
      name: 'Devon Lane',
      role: 'CEO at Groupon',
      text: 'As an educator, I\'ve used many management systems, but EduGenius stands out for its comprehensive excellent customer support.',
      image:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=200&auto=format&fit=crop',
    },
    {
      name: 'Esther Howard',
      role: 'CEO at Puzzle Huddle',
      text: 'Our school has seen a significant improvement in communication and collaboration since implementing EduGenius. Parents, teachers.',
      image:
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200&auto=format&fit=crop',
    },
  ];

  return (
    <section className="testimonials-section">
      <div className="testimonials-section__container">
        <div className="testimonials-section__header">
          <h2 className="testimonials-section__title">Customer Feedback</h2>
          <p className="testimonials-section__description">
            Hear Firsthand Accounts of How Our School Management Solution Has Revolutionized
            Education Management for Institutions Everywhere
          </p>
        </div>

        <div className="testimonials-section__grid">
          {reviews.map((review, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="testimonials-section__card"
            >
              <p className="testimonials-section__quote">"{review.text}"</p>
              <div className="testimonials-section__author">
                <img
                  src={review.image}
                  alt={review.name}
                  className="testimonials-section__avatar"
                />
                <div>
                  <h4 className="testimonials-section__author-name">{review.name}</h4>
                  <p className="testimonials-section__author-role">{review.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTA = () => {
  return (
    <section className="cta-section">
      <div className="cta-section__container">
        <h2 className="cta-section__title">It's easy to get started</h2>
        <div className="cta-section__reviews">
          <span className="cta-section__review-count">432,000+</span>
          <div className="cta-section__stars">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={14} className="cta-section__star" />
            ))}
          </div>
          <span className="cta-section__review-text">
            reviews on Google Play and App Store
          </span>
        </div>
        <div className="cta-section__buttons">
          <button className="cta-section__btn-primary">Get started</button>
          <button className="cta-section__btn-secondary">See How it works</button>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="footer">
      <div className="footer__container">
        <div className="footer__grid">
          <div className="footer__col footer__col--main">
            <div className="footer__logo">
              <div className="footer__logo-icon"></div>
              <span className="footer__logo-text">EduGenius</span>
            </div>
            <p className="footer__description">
              Simplifying Your School Management Hub, Your Ultimate SAAS Solution for
              Effortless School Administration
            </p>
            <div className="footer__social">
              <button onClick={(e) => e.preventDefault()} className="footer__social-link" aria-label="Instagram">
                <Instagram size={18} />
              </button>
              <button onClick={(e) => e.preventDefault()} className="footer__social-link" aria-label="Twitter">
                <Twitter size={18} />
              </button>
              <button onClick={(e) => e.preventDefault()} className="footer__social-link" aria-label="Github">
                <Github size={18} />
              </button>
              <button onClick={(e) => e.preventDefault()} className="footer__social-link" aria-label="Linkedin">
                <Linkedin size={18} />
              </button>
            </div>
          </div>

          <div className="footer__links-grid">
            <div>
              <h4 className="footer__link-title">Product</h4>
              <ul className="footer__link-list">
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    How it Works
                  </button>
                </li>
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    Services
                  </button>
                </li>
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    Blogs
                  </button>
                </li>
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    Press Info
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="footer__link-title">Company</h4>
              <ul className="footer__link-list">
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    FAQs
                  </button>
                </li>
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    Customer Stories
                  </button>
                </li>
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="footer__link-title">Utility</h4>
              <ul className="footer__link-list">
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    Download
                  </button>
                </li>
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    Help Center
                  </button>
                </li>
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    Events
                  </button>
                </li>
                <li>
                  <button onClick={(e) => e.preventDefault()} className="footer__link">
                    Developers
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="footer__bottom">
          <p className="footer__copyright">© All rights reserved By EduGenius</p>
          <div className="footer__bottom-links">
            <button onClick={(e) => e.preventDefault()} className="footer__bottom-link">
              Privacy Policy
            </button>
            <button onClick={(e) => e.preventDefault()} className="footer__bottom-link">
              Terms of Service
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

const NodeascendBadge = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.4 }}
      className="nodeascend-badge"
    >
      <div
        className="nodeascend-card"
        onClick={() => window.open('https://nodeascend.com', '_blank')}
        style={{ cursor: 'pointer' }}
      >
        <div className="nodeascend-logo">
          <img
            src={require('../assets/images/image.png')}
            alt="NodeAscend Logo"
            className="nodeascend-logo-img"
          />
        </div>
      </div>
    </motion.div>
  );
};

// --- Main HomePage Component ---

export default function HomePage() {
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();

  // If logged in, redirect to appropriate dashboard
  React.useEffect(() => {
    if (currentUser) {
      if (userRole === 'super_admin') {
        navigate('/super-admin-dashboard');
      } else if (['admin', 'principal'].includes(userRole)) {
        navigate('/admin-dashboard');
      } else if (userRole === 'teacher') {
        navigate('/teacher-dashboard');
      } else if (userRole === 'student') {
        navigate('/dashboard');
      }
    }
  }, [currentUser, userRole, navigate]);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSignupClick = () => {
    navigate('/register-institution');
  };

  return (
    <div className="home-page">
      <Navbar onLoginClick={handleLoginClick} onSignupClick={handleSignupClick} />
      <main className="home-page__main">
        <Hero onLoginClick={handleLoginClick} />
        <FeatureSection1 />
        <PortalServicesGrid />
        <DashboardPreview />
        <StepsSection />
        <Testimonials />
        <CTA />
      </main>
      <Footer />
      <NodeascendBadge />
    </div>
  );
}
